export type TaskGroupSelectionState = 'none' | 'some' | 'all'

/**
 * Tasks picked on the task dashboard for a bulk action. A group's "select all" only
 * covers the ids it is given, i.e. the tasks loaded on screen, never unloaded pages.
 *
 * A plain class, made reactive by the component holding it in `data()`: its methods,
 * called on that proxy, read and write the selected ids reactively.
 */
export class TaskSelection {
  selectedIds = new Set<string>()

  get count(): number {
    return this.selectedIds.size
  }

  ids(): string[] {
    return [...this.selectedIds]
  }

  isSelected(id: string): boolean {
    return this.selectedIds.has(id)
  }

  toggle(id: string): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id)
    } else {
      this.selectedIds.add(id)
    }
  }

  groupState(ids: string[]): TaskGroupSelectionState {
    const selectedCount = ids.filter((id) => this.selectedIds.has(id)).length
    if (selectedCount === 0) return 'none'
    return selectedCount === ids.length ? 'all' : 'some'
  }

  toggleGroup(ids: string[]): void {
    if (this.groupState(ids) === 'all') {
      ids.forEach((id) => this.selectedIds.delete(id))
    } else {
      ids.forEach((id) => this.selectedIds.add(id))
    }
  }

  remove(ids: string[]): void {
    ids.forEach((id) => this.selectedIds.delete(id))
  }

  clear(): void {
    this.selectedIds.clear()
  }
}
